import { CellWrapperProps } from "@/components/elements/Advanced/Table/types";
import { Elements } from "@/components/elements/Elements";
import { WeatherParameters } from "@/components/elements/Weather/types";
import { TodoType } from "@/services/todolist";
import { DBSchema, IDBPDatabase, IDBPTransaction } from "idb";
import type { Schema as V0004Schema } from "./v0004";

type Note = {
  id: string;
  title: string;
  icon: string;
  notebookId: string | null;
  content: any;
  createdAt: Date;
  updatedAt: Date;
  tags: string[];
};

type Notebook = {
  id: string;
  title: string;
  icon: string;
  notes?: Note[];
};

export interface Schema extends DBSchema {
  appState:
    | {
        key: `elements.visibility.${string}`;
        value: boolean | null;
      }
    | {
        key: "activeProfileId";
        value: string | null;
      }
    | {
        key: "ai.provider";
        value: string | null;
      };
  elements: {
    key: string;
    value: {
      id: string;
      name: keyof typeof Elements;
      parentId: string | null;
      childrenIds: string[];
      profileId: string | null;
      config: string;
    };
    indexes: {
      id: string;
      profileId: string;
    };
  };
  stickyNotes: {
    key: string;
    value: {
      elementId: string;
      content: string;
    };
  };
  weatherConfig: {
    key: string;
    value: {
      elementId: string;
    } & WeatherParameters;
  };
  todos: {
    key: string;
    value: TodoType;
    indexes: {
      id: string;
    };
  };
  notes: {
    key: string;
    value: Note;
    indexes: {
      id: string;
      notebookId: string;
    };
  };
  notebooks: {
    key: string;
    value: Notebook;
    indexes: {
      id: string;
    };
  };
  requests: {
    key: string;
    value: {
      id: string;
      url: string;
      method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
      isPaginated: boolean;
      pagination:
        | {
            type: "basic";
            countField: string;
          }
        | {
            type: "cursor";
            cursorType: "index" | "date";
          }
        | null;
      headers: {
        enabled: boolean;
        key: string;
        value: string;
      }[];
      queries: {
        enabled: boolean;
        pageField: boolean;
        perPageField: boolean;
        key: string;
        value: string;
      }[];
      auth:
        | {
            type: "none";
          }
        | {
            type: "basic";
            username: string;
            password: string;
          }
        | {
            type: "bearer";
            token: string;
            prefix: string;
          };
      body: string;
    };
    indexes: {
      id: string;
      url: string;
      method: string;
    };
  };
  tableConfigurations: {
    key: string;
    value: {
      id: string;
      requestId: string;
      arrayField: string;
      countField: string;
      title: string;
      columns: {
        enabled: boolean;
        fieldName: string;
        columnName: string;
        columnType:
          | "string"
          | "number"
          | "boolean"
          | "date"
          | "array"
          | "object"
          | "unknown";
        columnDisplay: string;
        columnDisplayProps: Record<string, unknown>;
        columnStyle: CellWrapperProps;
      }[];
    };
    indexes: {
      id: string;
      requestId: string;
    };
  };
}

export const migration = async (
  _: IDBPDatabase<V0004Schema>,
  tx: IDBPTransaction<V0004Schema>,
) => {
  const accessToken = await tx.objectStore("appState").get("auth.accessToken");
  if (accessToken) {
    localStorage.setItem("unideck.auth.accessToken", accessToken as string);
  }

  await (tx.objectStore("appState") as any).delete("auth.accessToken");
  await (tx.objectStore("appState") as any).delete("auth.user");
  await (tx.objectStore("appState") as any).delete("auth.dismissed");
};
